/**
 * Список проектов.
 *
 * Карточка отвечает на вопрос «что с проектом прямо сейчас» без перехода
 * внутрь: этап и его готовность, цель, блокеры, следующие действия, последняя
 * активность (документ «Структура интерфейса», §5).
 */

import {
  AlertOctagonIcon,
  ArrowRightIcon,
  FolderPlusIcon,
  PlusIcon,
  TargetIcon,
  UploadIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import type { ProjectSummaryDto } from '@project-os/contracts';
import { PROJECT_STATUS_LABELS_RU } from '@project-os/contracts';
import { useProjects } from '@/api/projects';
import { useSession } from '@/api/auth';
import { Progress } from '@/components/common/progress';
import { StatusBadge } from '@/components/common/status-badge';
import { ProjectLogo } from '@/components/common/project-logo';
import { CardListSkeleton, EmptyState, ErrorState } from '@/components/common/states';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { formatRelative, initials, pluralRu } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CreateProjectDialog } from './CreateProjectDialog';
import { ImportProjectDialog } from './ImportProjectDialog';

const STATUS_TONES = {
  IDEA: 'neutral',
  ACTIVE: 'success',
  PAUSED: 'warning',
  COMPLETED: 'neutral',
} as const;

function ProjectCard({ project }: { project: ProjectSummaryDto }) {
  const stage = project.currentStage;

  return (
    <Card className="surface-card-interactive gap-0 overflow-hidden py-0">
      <Link to={`/projects/${project.id}`} className="block p-4 focus-visible:outline-none">
        <div className="flex items-start justify-between gap-3">
          <ProjectLogo project={project} size="lg" className="mt-px" />
          <h3 className="min-w-0 flex-1 text-sm leading-snug font-semibold">{project.name}</h3>
          <StatusBadge
            label={PROJECT_STATUS_LABELS_RU[project.status]}
            tone={STATUS_TONES[project.status]}
          />
        </div>

        {project.currentGoal ? (
          <p className="mt-2 line-clamp-2 flex gap-1.5 text-xs leading-relaxed text-muted-foreground">
            <TargetIcon className="mt-0.5 size-3.5 shrink-0" />
            <span>{project.currentGoal}</span>
          </p>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground/70 italic">Цель не задана</p>
        )}

        <div className="mt-4">
          {stage ? (
            <>
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="truncate font-medium text-foreground">{stage.name}</span>
                {/* «0 из 0» — не готовность, а её отсутствие: требований
                    ещё не завели. Счёт показывается только когда есть что
                    считать. */}
                <span className="shrink-0 text-muted-foreground tabular-nums">
                  {stage.requirementsTotal > 0
                    ? `${stage.requirementsDone} из ${stage.requirementsTotal}`
                    : 'без требований'}
                </span>
              </div>
              <Progress
                className={cn('mt-1.5', stage.requirementsTotal === 0 && 'invisible')}
                value={stage.requirementsDone}
                max={stage.requirementsTotal}
                label={`Выполнено ${stage.requirementsDone} из ${pluralRu(stage.requirementsTotal, [
                  'требования',
                  'требований',
                  'требований',
                ])}`}
              />
            </>
          ) : (
            <p className="text-xs text-muted-foreground/70">Этап не задан</p>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
          {project.counters.openBlockers > 0 ? (
            <span className="inline-flex items-center gap-1 font-medium text-tone-danger">
              <AlertOctagonIcon className="size-3.5" />
              {pluralRu(project.counters.openBlockers, ['блокер', 'блокера', 'блокеров'])}
            </span>
          ) : null}
          <span>
            {pluralRu(project.counters.nextActions, [
              'следующее действие',
              'следующих действия',
              'следующих действий',
            ])}
          </span>
          <span>
            {pluralRu(project.counters.openQuestions, [
              'открытый вопрос',
              'открытых вопроса',
              'открытых вопросов',
            ])}
          </span>
        </div>
      </Link>

      <div className="flex items-center justify-between border-t bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
        <span className="flex min-w-0 items-center gap-1.5">
          <Avatar className="size-4.5">
            <AvatarFallback className="text-[9px]">{initials(project.owner.name)}</AvatarFallback>
          </Avatar>
          <span className="truncate">{project.owner.name}</span>
        </span>
        <span className="shrink-0" title="Последнее изменение">
          {formatRelative(project.lastActivityAt)}
        </span>
      </div>
    </Card>
  );
}

export function ProjectsPage() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { data: session } = useSession();
  const { data: projects, isPending, isError, error, refetch } = useProjects({ includeArchived });

  // Создавать проекты может любой, кто не является наблюдателем во всех
  // пространствах: сервер проверит право повторно.
  const canCreate = (session?.workspaces ?? []).some((workspace) => workspace.role !== 'VIEWER');

  // Импорт строже: он создаёт проект целиком, вместе с составом участников,
  // поэтому доступен владельцу или администратору пространства. Список
  // подходящих пространств заодно служит выбором, куда импортировать.
  const importTargets = (session?.workspaces ?? []).filter(
    (workspace) => workspace.role === 'OWNER' || workspace.role === 'ADMIN',
  );

  return (
    <>
      <PageHeader
        title="Проекты"
        description={
          projects ? pluralRu(projects.length, ['проект', 'проекта', 'проектов']) : 'Загрузка…'
        }
        actions={
          <>
            <Label className="gap-1.5 text-xs font-normal text-muted-foreground">
              <input
                type="checkbox"
                className="size-3.5 accent-primary"
                checked={includeArchived}
                onChange={(event) => setIncludeArchived(event.target.checked)}
              />
              Показывать архивные
            </Label>
            {importTargets.length > 0 ? (
              <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
                <UploadIcon />
                Импортировать проект
              </Button>
            ) : null}
            {canCreate ? (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <PlusIcon />
                Новый проект
              </Button>
            ) : null}
          </>
        }
      />

      <PageBody>
        {isPending ? (
          <CardListSkeleton count={3} />
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : projects.length === 0 ? (
          <EmptyState
            icon={<FolderPlusIcon />}
            title="Проектов пока нет"
            description="Проект объединяет знания, работу, активы, ресурсы и расходы команды в одном месте: что изучено, что решено, что готово и чего не хватает для следующего этапа."
            action={
              canCreate ? (
                <Button onClick={() => setCreateOpen(true)}>
                  <PlusIcon />
                  Создать первый проект
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Обратитесь к владельцу пространства, чтобы вас добавили в проект.
                </p>
              )
            }
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {projects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}

        {projects && projects.length > 0 ? (
          <p className="mt-6 flex items-center gap-1.5 text-xs text-muted-foreground">
            <ArrowRightIcon className="size-3.5" />
            Откройте проект, чтобы увидеть его состояние, знания и следующие действия.
          </p>
        ) : null}
      </PageBody>

      <CreateProjectDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        workspaces={session?.workspaces ?? []}
      />

      <ImportProjectDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        workspaces={importTargets}
      />
    </>
  );
}
